import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
  LinearProgress,
  Stack,
  Menu,
  Tooltip,
  Paper,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Schedule as ScheduleIcon,
  Preview as PreviewIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  LocalOffer as CouponIcon
} from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import contactService, { searchCustomersByPhone } from '../services/contactService';
import mediaService from '../services/mediaService';
import axios from '../utils/axiosConfig';
import CustomerSelectionModal from '../components/marketing/CustomerSelectionModal';
import {
  WhatsAppTemplate,
  SendMessageData,
  BulkSendData
} from '../types/whatsapp';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WhatsAppSendPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  
  // حالة رفع الملفات
  const [uploadingSingle, setUploadingSingle] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputSingleRef = useRef<HTMLInputElement>(null);
  const fileInputBulkRef = useRef<HTMLInputElement>(null);

  // Single message state
  const [singleMessage, setSingleMessage] = useState<SendMessageData>({
    phoneNumber: '',
    countryCode: '+966',
    messageType: 'template',
    templateId: '',
    content: '',
    variables: {},
    bodyValues: [],
    scheduledAt: undefined,
    languageCode: 'ar'
  });

  // Bulk message state
  const [bulkMessage, setBulkMessage] = useState<BulkSendData>({
    name: '',
    description: '',
    messageType: 'template',
    templateId: '',
    content: '',
    variables: {},
    recipients: [],
    campaignType: 'marketing',
    scheduledAt: undefined,
    languageCode: 'ar',
    bodyValues: [],
    headerValues: []
  });

  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [autoVarAnchorEl, setAutoVarAnchorEl] = useState<null | HTMLElement>(null);
  const [bulkAutoVarAnchorEl, setBulkAutoVarAnchorEl] = useState<null | HTMLElement>(null);

  // حالات الكوبونات الفريدة
  const [enableUniqueCoupons, setEnableUniqueCoupons] = useState(false);
  const [baseCouponId, setBaseCouponId] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState<any[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  // المتغيرات التلقائية المتاحة
  const autoVariables = [
    { key: 'اسم_العميل', label: 'اسم العميل', icon: '👤' },
    { key: 'رقم_الهاتف', label: 'رقم الهاتف', icon: '📱' },
    { key: 'الفرع', label: 'الفرع', icon: '🏢' },
    { key: 'كود_الخصم', label: 'كود الخصم', icon: '🎟️' },
  ];

  // إدراج متغير تلقائي في القيم
  const handleInsertAutoVar = (key: string, isBulk: boolean = false) => {
    const placeholder = `{{${key}}}`;
    if (isBulk) {
      setBulkMessage(prev => ({
        ...prev,
        bodyValues: [...(prev.bodyValues || []), placeholder]
      }));
    } else {
      setSingleMessage(prev => ({
        ...prev,
        bodyValues: [...(prev.bodyValues || []), placeholder]
      }));
    }
    setAutoVarAnchorEl(null);
    setBulkAutoVarAnchorEl(null);
  };

  // تطبيع رقم الهاتف لإزالة كل الفروقات في الصيغة
  const normalizePhone = (phone: string): string => {
    let clean = phone.replace(/\D/g, '');
    // إزالة الأصفار والكود المتكررة حتى يستقر
    let prev;
    do {
      prev = clean;
      clean = clean.replace(/^0+/, '');
      if (clean.startsWith('966')) {
        clean = clean.substring(3);
      }
    } while (clean !== prev);
    return clean;
  };

  // البحث عن جهة اتصال برقم الهاتف (أولاً محلياً، ثم من الخادم)
  const findContactByPhone = async (phoneNumber: string) => {
    const normalizedPhone = normalizePhone(phoneNumber);

    // البحث أولاً في جهات الاتصال المحلية
    const localContact = contacts.find(c => {
      if (!c.phone) return false;
      const normalizedContact = normalizePhone(c.phone);
      return normalizedContact === normalizedPhone;
    });

    if (localContact) return localContact;

    // إذا لم نجده محلياً، نبحث من الخادم
    try {
      const serverContacts = await searchCustomersByPhone(normalizedPhone);
      if (serverContacts && serverContacts.length > 0) {
        return serverContacts[0];
      }
    } catch (error) {
      console.error('خطأ في البحث عن العميل من الخادم:', error);
    }

    return undefined;
  };

  // استبدال المتغيرات التلقائية بالقيم الفعلية
  const resolveAutoVariables = async (bodyValues: string[], phoneNumber: string, contactName: string): Promise<string[]> => {
    // البحث عن جهة الاتصال للفرع (نبحث من الخادم أيضاً)
    const contactForBranch = await findContactByPhone(phoneNumber);

    return bodyValues.map(value => {
      let resolved = value;
      if (value.includes('{{اسم_العميل}}')) {
        resolved = resolved.replace(/\{\{اسم_العميل\}\}/g, contactName || '');
      }
      if (value.includes('{{رقم_الهاتف}}')) {
        resolved = resolved.replace(/\{\{رقم_الهاتف\}\}/g, phoneNumber || '');
      }
      if (value.includes('{{الفرع}}')) {
        const branchName = contactForBranch?.branches?.[0]?.name || '';
        resolved = resolved.replace(/\{\{الفرع\}\}/g, branchName);
      }
      if (value.includes('{{كود_الخصم}}')) {
        // كود الخصم سيتم استبداله لاحقاً من API
        resolved = resolved.replace(/\{\{كود_الخصم\}\}/g, value);
      }
      return resolved;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesData, contactsData, providersData] = await Promise.all([
        whatsappService.getTemplates(),
        contactService.getContacts(),
        whatsappService.getProviders()
      ]);
      setTemplates(templatesData);
      setContacts(contactsData);
      setProviders(providersData.filter((p: any) => p.isActive));
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // تحميل الكوبونات المتاحة
  const loadAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await axios.get('/coupons');
      if (response.data.success) {
        setAvailableCoupons(response.data.data.coupons || []);
      }
    } catch (error) {
      console.error('خطأ في تحميل الكوبونات:', error);
    } finally {
      setLoadingCoupons(false);
    }
  };

  // تحميل الكوبونات عند تفعيل خاصية الكوبونات الفريدة
  useEffect(() => {
    if (enableUniqueCoupons && availableCoupons.length === 0) {
      loadAvailableCoupons();
    }
  }, [enableUniqueCoupons]);

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t._id === templateId);
    setSelectedTemplate(template || null);
    
    if (template) {
      // Initialize variable values
      const vars: Record<string, string> = {};
      template.content.body.variables.forEach((v, index) => {
        vars[`var${index + 1}`] = '';
      });
      setVariableValues(vars);
    }
  };

  const handleSendSingle = async () => {
    try {
      setSending(true);

      // البحث عن جهة الاتصال الحالية
      const currentContact = await findContactByPhone(singleMessage.phoneNumber);
      const contactName = currentContact?.name || singleMessage.phoneNumber;

      // استخدام bodyValues إذا كانت متاحة (تحتوي على placeholders من المتغيرات التلقائية أو قيم يدوية)، وإلا استخدام variableValues
      const rawBodyValues = singleMessage.bodyValues && singleMessage.bodyValues.length > 0
        ? [...singleMessage.bodyValues]
        : (selectedTemplate?.content.body.variables.map((_, index) =>
            variableValues[`var${index + 1}`] || ''
          ) || []);

      // استبدال المتغيرات التلقائية بالقيم الفعلية
      const bodyValues = await resolveAutoVariables(rawBodyValues, singleMessage.phoneNumber, contactName);

      const data: SendMessageData = {
        ...singleMessage,
        bodyValues,
        templateName: selectedTemplate?.displayName,
        languageCode: singleMessage.languageCode || 'ar',
        contactId: currentContact?._id,
        contactName: currentContact?.name
      };

      const result = await whatsappService.sendMessage(data);
      showAlert('success', result.message || 'تم إرسال الرسالة بنجاح');
      
      // Reset form
      setSingleMessage({
        phoneNumber: '',
        countryCode: '+966',
        messageType: 'template',
        templateId: '',
        content: '',
        variables: {},
        bodyValues: [],
        languageCode: 'ar'
      });
      setVariableValues({});
      setSelectedTemplate(null);
    } catch (error: any) {
      showAlert('error', error.message || 'فشل إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  const handleSendBulk = async () => {
    if (bulkMessage.recipients.length === 0) {
      showAlert('error', 'الرجاء إضافة مستلمين');
      return;
    }

    // التحقق من اختيار الكوبون إذا كان مفعلاً
    if (enableUniqueCoupons && !baseCouponId) {
      showAlert('error', 'يجب اختيار كوبون النموذج عند تفعيل الكوبونات الفريدة');
      return;
    }

    try {
      setSending(true);

      // التحقق من وجود متغيرات تلقائية أو كود خصم تحتاج لحل
      const hasAutoVars = bulkMessage.bodyValues?.some(v =>
        v.includes('{{اسم_العميل}}') ||
        v.includes('{{رقم_الهاتف}}') ||
        v.includes('{{الفرع}}') ||
        v.includes('{{كود_الخصم}}')
      );

      // إذا كانت الكوبونات الفريدة مفعلة، نستخدم المسار الجديد
      if (enableUniqueCoupons && baseCouponId) {
        const result = await whatsappService.sendBulkMessagesWithCoupons({
          ...bulkMessage,
          baseCouponId
        });
        showAlert('success', result.message || 'تم إرسال الرسائل الجماعية بنجاح');
      } else if (hasAutoVars) {
        // إرسال فردي لكل مستلم مع حل المتغيرات التلقائية
        for (const recipient of bulkMessage.recipients) {
          const contactName = recipient.contactName || recipient.phoneNumber;
          const resolvedBodyValues = await resolveAutoVariables(
            bulkMessage.bodyValues || [],
            recipient.phoneNumber,
            contactName
          );

          const data: SendMessageData = {
            providerId: bulkMessage.providerId,
            phoneNumber: recipient.phoneNumber,
            countryCode: recipient.countryCode || '+966',
            messageType: bulkMessage.messageType,
            templateId: bulkMessage.templateId,
            templateName: bulkMessage.templateName,
            bodyValues: resolvedBodyValues,
            headerValues: bulkMessage.headerValues,
            buttonValues: bulkMessage.buttonValues,
            languageCode: bulkMessage.languageCode || 'ar',
            contactId: recipient.contactId,
            contactName: contactName
          };

          await whatsappService.sendMessage(data);
        }
        showAlert('success', 'تم إرسال الرسائل الجماعية بنجاح');
      } else {
        const result = await whatsappService.sendBulkMessages(bulkMessage);
        showAlert('success', result.message || 'تم بدء الإرسال الجماعي');
      }

      // Reset form
      setBulkMessage({
        name: '',
        description: '',
        messageType: 'template',
        templateId: '',
        content: '',
        variables: {},
        recipients: [],
        campaignType: 'marketing',
        bodyValues: [],
        headerValues: []
      });
      setEnableUniqueCoupons(false);
      setBaseCouponId('');
    } catch (error: any) {
      showAlert('error', error.message || 'فشل الإرسال الجماعي');
    } finally {
      setSending(false);
    }
  };

  const handleSelectCustomers = async (phoneNumbers: string[]) => {
     const newRecipients = await Promise.all(phoneNumbers.map(async (phone) => {
       const contact = await findContactByPhone(phone);
       return {
         phoneNumber: phone.replace(/^966/, '').replace(/^0/, ''),
         countryCode: '+966',
         contactId: contact?._id,
         contactName: contact?.name
       };
     }));

    setBulkMessage({
      ...bulkMessage,
      recipients: newRecipients
    });

    setShowCustomerModal(false);
  };

  const handleRemoveRecipient = (index: number) => {
    setBulkMessage({
      ...bulkMessage,
      recipients: bulkMessage.recipients.filter((_, i) => i !== index)
    });
  };

  const handlePreview = async () => {
     let content = '';

     if (tabValue === 0) {
       // Single message preview
       if (singleMessage.messageType === 'template' && selectedTemplate) {
         content = selectedTemplate.content.body.text;
         // استخدام bodyValues إذا كانت متاحة، وإلا استخدام variableValues
         const previewBodyValues = singleMessage.bodyValues && singleMessage.bodyValues.length > 0
           ? [...singleMessage.bodyValues]
           : (selectedTemplate.content.body.variables.map((_, index) =>
               variableValues[`var${index + 1}`] || `{${index + 1}}`
             ));
         // استبدال المتغيرات التلقائية
         const currentContact = await findContactByPhone(singleMessage.phoneNumber);
         const contactName = currentContact?.name || singleMessage.phoneNumber;
         const resolvedValues = await resolveAutoVariables(previewBodyValues, singleMessage.phoneNumber, contactName);
         selectedTemplate.content.body.variables.forEach((_, index) => {
           const value = resolvedValues[index] || `{${index + 1}}`;
           content = content.replace(`{{${index + 1}}}`, value);
         });
       } else {
         content = singleMessage.content || '';
       }
     } else {
       // Bulk message preview
       if (bulkMessage.messageType === 'template' && selectedTemplate) {
         content = selectedTemplate.content.body.text;
         // عرض معاينة باستخدام المتغيرات التلقائية أو القيم اليدوية
         const previewBodyValues = bulkMessage.bodyValues && bulkMessage.bodyValues.length > 0
           ? [...bulkMessage.bodyValues]
           : (selectedTemplate.content.body.variables.map((_, index) =>
               variableValues[`var${index + 1}`] || `{${index + 1}}`
             ));
         const currentContact = bulkMessage.recipients[0]
           ? (await findContactByPhone(bulkMessage.recipients[0].phoneNumber) || bulkMessage.recipients[0])
           : null;
         const contactName = currentContact?.name || (bulkMessage.recipients[0]?.phoneNumber || '');
         const resolvedValues = await resolveAutoVariables(previewBodyValues, bulkMessage.recipients[0]?.phoneNumber || '', contactName);
         selectedTemplate.content.body.variables.forEach((_, index) => {
           const value = resolvedValues[index] || `{${index + 1}}`;
           content = content.replace(`{{${index + 1}}}`, value);
         });
       } else {
         content = bulkMessage.content || '';
       }
     }

     setPreviewContent(content);
     setPreviewOpen(true);
   };

  // رفع ملف للرسالة الفردية
  const handleUploadSingleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // التحقق من نوع الملف
    if (!mediaService.isValidFileType(file)) {
      showAlert('error', 'نوع الملف غير مدعوم. الأنواع المسموحة: صور، فيديو، مستندات');
      return;
    }

    // التحقق من حجم الملف
    if (!mediaService.isValidFileSize(file, 16)) {
      showAlert('error', 'حجم الملف كبير جداً. الحد الأقصى: 16 MB');
      return;
    }

    try {
      setUploadingSingle(true);
      setUploadProgress(0);

      const result = await mediaService.uploadFile(file);
      
      if (result.success) {
        setSingleMessage({
          ...singleMessage,
          headerValues: [result.file.url]
        });
        showAlert('success', 'تم رفع الملف بنجاح');
      }
    } catch (error: any) {
      showAlert('error', error.message || 'فشل رفع الملف');
    } finally {
      setUploadingSingle(false);
      setUploadProgress(0);
      if (fileInputSingleRef.current) {
        fileInputSingleRef.current.value = '';
      }
    }
  };

  // رفع ملف للرسالة الجماعية
  const handleUploadBulkFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!mediaService.isValidFileType(file)) {
      showAlert('error', 'نوع الملف غير مدعوم. الأنواع المسموحة: صور، فيديو، مستندات');
      return;
    }

    if (!mediaService.isValidFileSize(file, 16)) {
      showAlert('error', 'حجم الملف كبير جداً. الحد الأقصى: 16 MB');
      return;
    }

    try {
      setUploadingBulk(true);
      setUploadProgress(0);

      const result = await mediaService.uploadFile(file);
      
      if (result.success) {
        setBulkMessage({
          ...bulkMessage,
          headerValues: [result.file.url]
        });
        showAlert('success', 'تم رفع الملف بنجاح');
      }
    } catch (error: any) {
      showAlert('error', error.message || 'فشل رفع الملف');
    } finally {
      setUploadingBulk(false);
      setUploadProgress(0);
      if (fileInputBulkRef.current) {
        fileInputBulkRef.current.value = '';
      }
    }
  };

  // حذف الملف المرفوع للرسالة الفردية
  const handleRemoveSingleFile = () => {
    setSingleMessage({
      ...singleMessage,
      headerValues: []
    });
  };

  // حذف الملف المرفوع للرسالة الجماعية
  const handleRemoveBulkFile = () => {
    setBulkMessage({
      ...bulkMessage,
      headerValues: []
    });
  };

  const countryCodes = [
    { code: '+966', label: '🇸🇦 السعودية' },
    { code: '+971', label: '🇦🇪 الإمارات' },
    { code: '+965', label: '🇰🇼 الكويت' },
    { code: '+973', label: '🇧🇭 البحرين' },
    { code: '+974', label: '🇶🇦 قطر' },
    { code: '+968', label: '🇴🇲 عمان' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Typography variant="h4" component="h1" gutterBottom>
          إرسال رسائل واتساب
        </Typography>

        {/* Alert */}
        {alert && (
          <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        {/* Tabs */}
        <Card>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="إرسال رسالة واحدة" />
            <Tab label="إرسال جماعي" />
          </Tabs>

          {/* Single Message Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Provider Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>مزود الخدمة</InputLabel>
                  <Select
                    value={singleMessage.providerId || ''}
                    onChange={(e) => setSingleMessage({ ...singleMessage, providerId: e.target.value })}
                  >
                    {providers.map((provider) => (
                      <MenuItem key={provider._id} value={provider._id}>
                        {provider.name} ({provider.providerType})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الرسالة</InputLabel>
                  <Select
                    value={singleMessage.messageType}
                    onChange={(e) => setSingleMessage({ ...singleMessage, messageType: e.target.value as any })}
                  >
                    <MenuItem value="template">قالب معتمد</MenuItem>
                    <MenuItem value="text">رسالة نصية</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {singleMessage.messageType === 'template' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="اسم القالب"
                      value={singleMessage.templateId}
                      onChange={(e) => setSingleMessage({ ...singleMessage, templateId: e.target.value })}
                      placeholder="أدخل اسم القالب من لوحة Interakt"
                      helperText="أدخل اسم القالب كما هو معرّف في لوحة تحكم Interakt"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>لغة القالب</InputLabel>
                      <Select
                        value={singleMessage.languageCode || 'ar'}
                        onChange={(e) => setSingleMessage({ ...singleMessage, languageCode: e.target.value })}
                        label="لغة القالب"
                      >
                        <MenuItem value="ar">🇸🇦 العربية (ar)</MenuItem>
                        <MenuItem value="en">🇬🇧 الإنجليزية (en)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Template Variables - Manual Entry */}
              {singleMessage.messageType === 'template' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        متغيرات القالب (اختياري):
                      </Typography>
                      <Tooltip title="إدراج متغير تلقائي من جهات الاتصال">
                        <IconButton
                          size="small"
                          onClick={(e) => setAutoVarAnchorEl(e.currentTarget)}
                          color="primary"
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={autoVarAnchorEl}
                        open={Boolean(autoVarAnchorEl)}
                        onClose={() => setAutoVarAnchorEl(null)}
                      >
                        {autoVariables.map((av) => (
                          <MenuItem
                            key={av.key}
                            onClick={() => handleInsertAutoVar(av.key, false)}
                          >
                            {av.icon} {av.label} {'{{' + av.key + '}}'}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      أدخل قيم المتغيرات بنفس ترتيبها في القالب، مفصولة بفاصلة
                    </Typography>
                    <TextField
                      fullWidth
                      label="قيم المتغيرات"
                      value={singleMessage.bodyValues?.join(', ') || ''}
                      onChange={(e) => setSingleMessage({
                        ...singleMessage,
                        bodyValues: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                      })}
                      placeholder="مثال: أحمد, 100, 2024-01-01"
                      helperText="أدخل قيم المتغيرات مفصولة بفاصلة أو استخدم المتغيرات التلقائية"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      صورة/فيديو/مستند الرأس (اختياري):
                    </Typography>
                    
                    <Stack spacing={2}>
                      {/* زر رفع الملف */}
                      <Box>
                        <input
                          ref={fileInputSingleRef}
                          type="file"
                          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                          style={{ display: 'none' }}
                          onChange={handleUploadSingleFile}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => fileInputSingleRef.current?.click()}
                          disabled={uploadingSingle}
                          fullWidth
                        >
                          {uploadingSingle ? 'جاري الرفع...' : 'رفع ملف من جهازك'}
                        </Button>
                        {uploadingSingle && (
                          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                        )}
                      </Box>

                      {/* عرض الملف المرفوع */}
                      {singleMessage.headerValues?.[0] && (
                        <Box
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'success.main',
                            borderRadius: 1,
                            bgcolor: 'success.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ImageIcon color="success" />
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {singleMessage.headerValues[0]}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={handleRemoveSingleFile} color="error">
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}

                      {/* أو إدخال رابط يدوياً */}
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        - أو -
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="أدخل رابط الملف يدوياً"
                        value={singleMessage.headerValues?.[0] || ''}
                        onChange={(e) => setSingleMessage({
                          ...singleMessage,
                          headerValues: e.target.value ? [e.target.value] : []
                        })}
                        placeholder="https://example.com/image.jpg"
                        helperText="إذا كان لديك رابط جاهز، يمكنك إدخاله هنا مباشرة"
                        size="small"
                      />
                    </Stack>
                  </Grid>
                </>
              )}

              {/* Text Message */}
              {singleMessage.messageType === 'text' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="نص الرسالة"
                    value={singleMessage.content}
                    onChange={(e) => setSingleMessage({ ...singleMessage, content: e.target.value })}
                    placeholder="اكتب رسالتك هنا..."
                  />
                </Grid>
              )}

              {/* Phone Number */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>كود الدولة</InputLabel>
                  <Select
                    value={singleMessage.countryCode}
                    onChange={(e) => setSingleMessage({ ...singleMessage, countryCode: e.target.value })}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="رقم الهاتف"
                  value={singleMessage.phoneNumber}
                  onChange={(e) => setSingleMessage({ ...singleMessage, phoneNumber: e.target.value })}
                  placeholder="5xxxxxxxx"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSendSingle}
                    disabled={sending || !singleMessage.phoneNumber}
                  >
                    {sending ? 'جاري الإرسال...' : 'إرسال'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                  >
                    معاينة
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Bulk Message Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              {/* Provider Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>مزود الخدمة</InputLabel>
                  <Select
                    value={bulkMessage.providerId || ''}
                    onChange={(e) => setBulkMessage({ ...bulkMessage, providerId: e.target.value })}
                  >
                    {providers.map((provider) => (
                      <MenuItem key={provider._id} value={provider._id}>
                        {provider.name} ({provider.providerType})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="اسم الحملة"
                  value={bulkMessage.name}
                  onChange={(e) => setBulkMessage({ ...bulkMessage, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الحملة</InputLabel>
                  <Select
                    value={bulkMessage.campaignType}
                    onChange={(e) => setBulkMessage({ ...bulkMessage, campaignType: e.target.value as any })}
                  >
                    <MenuItem value="marketing">تسويقية</MenuItem>
                    <MenuItem value="transactional">معاملات</MenuItem>
                    <MenuItem value="notification">إشعارات</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="وصف الحملة (اختياري)"
                  value={bulkMessage.description}
                  onChange={(e) => setBulkMessage({ ...bulkMessage, description: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الرسالة</InputLabel>
                  <Select
                    value={bulkMessage.messageType}
                    onChange={(e) => setBulkMessage({ ...bulkMessage, messageType: e.target.value as any })}
                  >
                    <MenuItem value="template">قالب معتمد</MenuItem>
                    <MenuItem value="text">رسالة نصية</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {bulkMessage.messageType === 'template' && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="اسم القالب"
                      value={bulkMessage.templateId}
                      onChange={(e) => setBulkMessage({ ...bulkMessage, templateId: e.target.value })}
                      placeholder="أدخل اسم القالب من لوحة Interakt"
                      helperText="أدخل اسم القالب كما هو معرّف في لوحة تحكم Interakt"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>لغة القالب</InputLabel>
                      <Select
                        value={bulkMessage.languageCode || 'ar'}
                        onChange={(e) => setBulkMessage({ ...bulkMessage, languageCode: e.target.value })}
                        label="لغة القالب"
                      >
                        <MenuItem value="ar">🇸🇦 عربي</MenuItem>
                        <MenuItem value="en">🇬🇧 إنجليزي</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Template Variables for Bulk */}
              {bulkMessage.messageType === 'template' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        متغيرات القالب (اختياري):
                      </Typography>
                      <Tooltip title="إدراج متغير تلقائي من جهات الاتصال">
                        <IconButton
                          size="small"
                          onClick={(e) => setBulkAutoVarAnchorEl(e.currentTarget)}
                          color="primary"
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={bulkAutoVarAnchorEl}
                        open={Boolean(bulkAutoVarAnchorEl)}
                        onClose={() => setBulkAutoVarAnchorEl(null)}
                      >
                        {autoVariables.map((av) => (
                          <MenuItem
                            key={av.key}
                            onClick={() => handleInsertAutoVar(av.key, true)}
                          >
                            {av.icon} {av.label} {'{{' + av.key + '}}'}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      أدخل قيم المتغيرات بنفس ترتيبها في القالب، مفصولة بفاصلة
                    </Typography>
                    <TextField
                      fullWidth
                      label="قيم متغيرات النص (Body Values)"
                      value={bulkMessage.bodyValues?.join(', ') || ''}
                      onChange={(e) => setBulkMessage({
                        ...bulkMessage,
                        bodyValues: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                      })}
                      placeholder="مثال: أحمد, 100, 2024-01-01"
                      helperText="أدخل قيم المتغيرات مفصولة بفاصلة أو استخدم المتغيرات التلقائية"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      صورة/فيديو/مستند الرأس (اختياري):
                    </Typography>
                    
                    <Stack spacing={2}>
                      {/* زر رفع الملف */}
                      <Box>
                        <input
                          ref={fileInputBulkRef}
                          type="file"
                          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                          style={{ display: 'none' }}
                          onChange={handleUploadBulkFile}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => fileInputBulkRef.current?.click()}
                          disabled={uploadingBulk}
                          fullWidth
                        >
                          {uploadingBulk ? 'جاري الرفع...' : 'رفع ملف من جهازك'}
                        </Button>
                        {uploadingBulk && (
                          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                        )}
                      </Box>

                      {/* عرض الملف المرفوع */}
                      {bulkMessage.headerValues?.[0] && (
                        <Box
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'success.main',
                            borderRadius: 1,
                            bgcolor: 'success.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ImageIcon color="success" />
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {bulkMessage.headerValues[0]}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={handleRemoveBulkFile} color="error">
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}

                      {/* أو إدخال رابط يدوياً */}
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        - أو -
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="أدخل رابط الملف يدوياً"
                        value={bulkMessage.headerValues?.[0] || ''}
                        onChange={(e) => setBulkMessage({
                          ...bulkMessage,
                          headerValues: e.target.value ? [e.target.value] : []
                        })}
                        placeholder="https://example.com/image.jpg"
                        helperText="إذا كان لديك رابط جاهز، يمكنك إدخاله هنا مباشرة"
                        size="small"
                      />
                    </Stack>
                  </Grid>
                </>
              )}

              {bulkMessage.messageType === 'text' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="نص الرسالة"
                    value={bulkMessage.content}
                    onChange={(e) => setBulkMessage({ ...bulkMessage, content: e.target.value })}
                  />
                </Grid>
              )}

              {/* Recipients */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  المستلمون ({bulkMessage.recipients.length})
                </Typography>
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowCustomerModal(true)}
                  startIcon={<AddIcon />}
                  sx={{ mb: 2 }}
                >
                  إضافة مستلمين من العملاء
                </Button>

                {bulkMessage.recipients.length > 0 ? (
                   <Box sx={{
                     display: 'flex',
                     flexWrap: 'wrap',
                     gap: 1,
                     maxHeight: '200px',
                     overflowY: 'auto',
                     p: 2,
                     border: '1px solid',
                     borderColor: 'divider',
                     borderRadius: 1
                   }}>
                     {bulkMessage.recipients.map((recipient, index) => (
                       <Chip
                         key={index}
                         label={recipient.phoneNumber}
                         onDelete={() => handleRemoveRecipient(index)}
                         deleteIcon={<DeleteIcon />}
                         color="primary"
                         variant="outlined"
                         dir="ltr"
                       />
                     ))}
                   </Box>
                 ) : (
                   <Alert severity="info">
                     لم يتم إضافة أي مستلمين بعد. اضغط على الزر أعلاه لاختيار العملاء.
                   </Alert>
                 )}
               </Grid>

               {/* قسم الكوبونات الفريدة */}
               <Grid item xs={12}>
                 <Paper sx={{ p: 3 }}>
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <CouponIcon color="primary" sx={{ mr: 1 }} />
                     <Typography variant="h6">
                       خاصية الكوبونات الفريدة
                     </Typography>
                   </Box>
                   
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={enableUniqueCoupons}
                         onChange={(e) => setEnableUniqueCoupons(e.target.checked)}
                         color="primary"
                       />
                     }
                     label="إنشاء كوبون فريد لكل رقم مرسل (6-7 أرقام)"
                   />
                   
                   {enableUniqueCoupons && (
                     <FormControl fullWidth sx={{ mt: 2 }}>
                       <InputLabel>اختر كوبون النموذج</InputLabel>
                       <Select
                         value={baseCouponId}
                         onChange={(e) => setBaseCouponId(e.target.value)}
                         label="اختر كوبون النموذج"
                         disabled={loadingCoupons}
                       >
                         {availableCoupons.map((coupon) => (
                           <MenuItem key={coupon._id} value={coupon._id}>
                             {coupon.code} - {coupon.discountValue}
                             {coupon.discountType === 'percentage' ? '%' : ' ريال'}
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                   )}
                   
                   {enableUniqueCoupons && (
                     <Alert severity="info" sx={{ mt: 2 }}>
                       سيتم إنشاء كوبون فريد مكوّن من 6-7 أرقام لكل رقم هاتف في القائمة.
                       استخدم <strong>{'{كود_الخصم}'}</strong> في قيم متغيرات النص لإضافة الكود تلقائياً.
                     </Alert>
                   )}
                 </Paper>
               </Grid>

               {/* Scheduling */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  جدولة الإرسال (اختياري)
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="تاريخ ووقت الإرسال"
                  value={bulkMessage.scheduledAt || ''}
                  onChange={(e) => setBulkMessage({ ...bulkMessage, scheduledAt: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="اترك فارغاً للإرسال الفوري، أو حدد تاريخ ووقت للإرسال المجدول"
                />
              </Grid>

              {/* Send Rate */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="معدل الإرسال (رسالة/ثانية)"
                  value={bulkMessage.sendRate || 10}
                  onChange={(e) => setBulkMessage({ ...bulkMessage, sendRate: parseInt(e.target.value) || 10 })}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="عدد الرسائل المرسلة في الثانية (افتراضي: 10)"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={bulkMessage.scheduledAt ? <ScheduleIcon /> : <SendIcon />}
                    onClick={handleSendBulk}
                    disabled={sending || bulkMessage.recipients.length === 0 || !bulkMessage.name}
                  >
                    {sending ? 'جاري المعالجة...' : bulkMessage.scheduledAt ? 'جدولة الإرسال' : 'إرسال جماعي'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                  >
                    معاينة
                  </Button>
                  {bulkMessage.scheduledAt && (
                    <Chip
                      icon={<ScheduleIcon />}
                      label={`مجدول: ${new Date(bulkMessage.scheduledAt).toLocaleString('ar-SA')}`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>معاينة الرسالة</DialogTitle>
          <DialogContent>
            <Box sx={{ 
              p: 2, 
              bgcolor: '#e5ddd5', 
              borderRadius: 2,
              minHeight: 100
            }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {previewContent}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>إغلاق</Button>
          </DialogActions>
        </Dialog>

        {/* Customer Selection Modal */}
        <CustomerSelectionModal
          open={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          onSelectCustomers={handleSelectCustomers}
          existingRecipients={bulkMessage.recipients.map(r => r.phoneNumber)}
        />
      </Box>
  );
};

export default WhatsAppSendPage;